import { useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { SidebarFooterCard, SidebarMenu } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import { useIsInstitutionAdmin } from '@backrs_ui/grow-ui/src/hooks/useIsInstitutionAdmin';
import { useOperatorInstitutionSwitch } from '@backrs_ui/grow-ui/src/hooks/useOperatorInstitutionSwitch';
import { SEARCH_PAGE_CATEGORIES } from '@backrs_ui/grow-ui/src/pages/Search/search.types';
import { fetchNuggetsUserHasSearched } from '@backrs_ui/grow-ui/src/pages/Search/search.util';
import { selectHasNewVersion } from '@backrs_ui/grow-ui/src/redux/appState/appState';
import {
  useGetAllSessionsQuery,
  LubavSession,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import { selectIsEmulating } from '@backrs_ui/grow-ui/src/redux/emulator/emulator';
import {
  FEATURE_FLAGS,
  selectIsFeatureFlagEnabled,
} from '@backrs_ui/grow-ui/src/redux/featureFlags/featureFlags';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getActiveLubavSession,
  getSessionState,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import {
  selectHeldRoots,
  selectStoredOperatorRootId,
  type HeldRoot,
} from '@backrs_ui/grow-ui/src/redux/operatorContext';
import {
  UserProfileResponse,
  useFetchUserProfileInfoQuery,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';
import { setSearchTermForCategory } from '@backrs_ui/grow-ui/src/redux/search/search';
import { selectUser } from '@backrs_ui/grow-ui/src/redux/user/user';
import { useFetchUserPermissionsQuery } from '@backrs_ui/grow-ui/src/redux/user/user.api';
import { UIPermissionList } from '@backrs_ui/grow-ui/src/redux/user/user.transformer';
import { SANDBOX_CONTACT_FORM_URL } from '@backrs_ui/grow-ui/src/settings';
import { IS_SANDBOX } from '@backrs_ui/grow-ui/src/utils/sandboxSession';
import { BUILD_NUMBER, VERSION } from '@backrs_ui/grow-ui/src/version/version';

import { useMediaQuery } from '@mui/material';
import { usePostHog } from 'posthog-js/react';

import { popupSectionMenuItems } from './popupSection';
import { SidebarItems } from './SidebarItems';

const NEW_VERSION_STRINGS = appStrings.modules.backrsPageShell.menuItems.sidebar.newVersion;
const SANDBOX_STRINGS = appStrings.modules.sandboxPromptModal;

export type SectionDependencies = Readonly<{
  navigate: ReturnType<typeof useNavigate>;
  location: ReturnType<typeof useLocation>;
  dispatch: ReturnType<typeof useAppDispatch>;
  doesUserHaveBlueprint: boolean;
  guestHasBlueprint: boolean;
  userPermissions?: UIPermissionList;
  postHog: ReturnType<typeof usePostHog>;
  isUnauthenticated: boolean;
  isGuest: boolean;
  userId?: number;
  academicEnrollmentTypes: UserProfileResponse['profile']['academicEnrollmentTypes'];
  onOpenAssigneeEmulationPalette?: () => void;
  lubavSessions: LubavSession[];
  hasMoreSessions: boolean;
  onLoadMoreSessions: () => void;
  isLubavLoading: boolean;
  isMobile: boolean;
  hasNewVersion: boolean;
  isCoachingEnabled: boolean;
  isPaywalled: boolean;
  isPanelCollapsed: boolean;
  onExpandPanel: () => void;
  isInstitutionAdmin: boolean;
  /** Read by the caller, not by the section builders: those are plain functions,
   * and a hook called from one would run inside this memo. */
  heldRoots: readonly HeldRoot[];
  operatorInstitutionId: number | null;
  onOperatorInstitutionSelect: (institutionId: number) => void;
  onOpenGettingStarted: () => void;
  onNewChat: () => void;
  isEmulating: boolean;
}>;

export const useMenuSections = (
  isPaywalled = false,
  isPanelCollapsed = false,
  onExpandPanel: () => void = () => undefined,
  onOpenGettingStarted: () => void = () => undefined,
  onNewChat: () => void = () => undefined,
  isHeaderSearchShown = false,
): {
  sidebarMenu: SidebarMenu | undefined;
  isAssigneeEmulationPaletteOpen: boolean;
  setIsAssigneeEmulationPaletteOpen: (open: boolean) => void;
} => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const postHog = usePostHog();

  const [isAssigneeEmulationPaletteOpen, setIsAssigneeEmulationPaletteOpen] = useState(false);

  const handleOpenAssigneeEmulationPalette = useCallback(() => {
    setIsAssigneeEmulationPaletteOpen(true);
  }, []);

  const unauthenticatedUser = {
    id: 0,
    access_token: '',
    email: '',
    first_name: 'Guest',
    last_name: 'User',
    profile_image: undefined,
    location: undefined,
    job_title: undefined,
    isUnauthenticated: true,
  };
  const user = useAppSelector(selectUser) || unauthenticatedUser;
  const { isGuest } = useAuth();

  const { data: userPermissions } = useFetchUserPermissionsQuery(undefined, {
    skip: !user?.id,
  });

  const doesUserHaveBlueprint = useAppSelector((state) => !!selectUser(state)?.has_blueprint);
  const guestHasBlueprint = useAppSelector(
    (state) => !!state.userResponseSet.mostRecentUserResponseSet?.user_responses?.length,
  );

  const { data: profileData } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS, {
    skip: !user?.id,
  });
  const academicEnrollmentTypes = profileData?.profile.academicEnrollmentTypes;

  const [sessionsOffset, setSessionsOffset] = useState(0);

  const { data: sessionsData } = useGetAllSessionsQuery(
    { limit: 10, offset: sessionsOffset },
    { skip: !user?.id },
  );

  const lubavSessions = useMemo(() => sessionsData?.sessions ?? [], [sessionsData?.sessions]);
  const hasMoreSessions = sessionsData?.has_more ?? false;

  const handleLoadMoreSessions = useCallback(() => {
    if (!hasMoreSessions) return;

    setSessionsOffset(lubavSessions.length);
  }, [hasMoreSessions, lubavSessions.length]);

  const activeSession = useAppSelector(getActiveLubavSession);
  const isStreaming = useAppSelector(
    (state) => getSessionState(state, activeSession)?.coachLubav?.streaming ?? false,
  );
  const isTyping = useAppSelector(
    (state) => getSessionState(state, activeSession)?.coachLubav?.loading ?? false,
  );
  const isLubavLoading = isStreaming || isTyping;

  const isMobile = useMediaQuery('(max-width: 600px)');
  const hasNewVersion = useAppSelector(selectHasNewVersion);
  const isCoachingEnabled = useAppSelector(selectIsFeatureFlagEnabled(FEATURE_FLAGS.COACHING));
  const isEmulating = useAppSelector(selectIsEmulating);
  const isInstitutionAdmin = useIsInstitutionAdmin();

  const heldRoots = useAppSelector(selectHeldRoots);
  const operatorInstitutionId = useAppSelector(selectStoredOperatorRootId);
  const onOperatorInstitutionSelect = useOperatorInstitutionSwitch();

  return useMemo(() => {
    const handleSearch = (searchTerm?: string) => {
      if (searchTerm) {
        dispatch(
          setSearchTermForCategory({ category: SEARCH_PAGE_CATEGORIES.DEFAULT, searchTerm }),
        );

        fetchNuggetsUserHasSearched(dispatch, searchTerm, 1);
      }

      navigate(APP_ROUTE.SEARCH);
    };

    const sectionHookProps: SectionDependencies = {
      heldRoots,
      operatorInstitutionId,
      onOperatorInstitutionSelect,
      navigate,
      location,
      dispatch,
      doesUserHaveBlueprint,
      guestHasBlueprint,
      userPermissions: userPermissions || undefined,
      postHog,
      isUnauthenticated: user?.isUnauthenticated || false,
      isGuest,
      userId: user?.id,
      academicEnrollmentTypes,
      onOpenAssigneeEmulationPalette: handleOpenAssigneeEmulationPalette,
      lubavSessions,
      hasMoreSessions,
      onLoadMoreSessions: handleLoadMoreSessions,
      isLubavLoading,
      isMobile,
      hasNewVersion,
      isCoachingEnabled,
      isPaywalled,
      isPanelCollapsed,
      onExpandPanel,
      isInstitutionAdmin,
      onOpenGettingStarted,
      onNewChat,
      isEmulating,
    };

    const runningVersion = `${VERSION}.${BUILD_NUMBER}`;

    const sandboxContactFooterCard: SidebarFooterCard | undefined = IS_SANDBOX
      ? {
          id: 'sidemenu-sandbox-contact',
          heading: SANDBOX_STRINGS.title,
          ctaLabel: SANDBOX_STRINGS.cta,
          ctaOnClick: () => window.open(SANDBOX_CONTACT_FORM_URL, '_blank'),
        }
      : undefined;

    const newVersionFooterCard: SidebarFooterCard | undefined =
      hasNewVersion && !isPaywalled
        ? {
            id: 'sidemenu-new-version',
            heading: NEW_VERSION_STRINGS.heading,
            subtitle: NEW_VERSION_STRINGS.subtitleTemplate.replace('{version}', runningVersion),
            ctaLabel: NEW_VERSION_STRINGS.ctaLabel,
            ctaOnClick: () => {
              postHog?.capture('version_update_clicked', {
                surface: 'sidebar',
                version: runningVersion,
              });
              window.location.reload();
            },
          }
        : undefined;

    const sidebarMenu = {
      search: {
        onSearch: handleSearch,
        isPageActive: location.pathname === APP_ROUTE.SEARCH,
      },
      hideSearch: isPaywalled || isHeaderSearchShown,
      user,
      goToHome: () => navigate(APP_ROUTE.HOME),
      accountMenuItems: popupSectionMenuItems({ ...sectionHookProps }),
      sections: [{ menuItems: SidebarItems(sectionHookProps) }],
      footerCard: sandboxContactFooterCard ?? newVersionFooterCard,
    };

    return {
      sidebarMenu,
      isAssigneeEmulationPaletteOpen,
      setIsAssigneeEmulationPaletteOpen,
    };
  }, [
    navigate,
    location,
    dispatch,
    doesUserHaveBlueprint,
    guestHasBlueprint,
    userPermissions,
    postHog,
    user,
    isGuest,
    academicEnrollmentTypes,
    handleOpenAssigneeEmulationPalette,
    isAssigneeEmulationPaletteOpen,
    lubavSessions,
    hasMoreSessions,
    handleLoadMoreSessions,
    isLubavLoading,
    isMobile,
    hasNewVersion,
    isCoachingEnabled,
    isPaywalled,
    isPanelCollapsed,
    onExpandPanel,
    isInstitutionAdmin,
    heldRoots,
    operatorInstitutionId,
    onOperatorInstitutionSelect,
    onOpenGettingStarted,
    onNewChat,
    isEmulating,
    isHeaderSearchShown,
  ]);
};
